/**
 * @fileoverview Job Activity panel — per-job time breakdown.
 *
 * Read-only "Activity" tab in job settings. Shows how much wall-clock time
 * has been spent on the job, split across:
 * - Agents (processing) — recorded summarisation run durations
 * - Agent chats — per-agent summary Q&A engagement
 * - General job chat — universal job-scoped agent engagement
 * - Contact chats — per-contact CRM chat engagement
 *
 * Chat "time" is active engagement (gaps over 5 min excluded). Computed on
 * demand via `api.jobs.activity.getJobTimeBreakdown` (an action), so the
 * panel fetches on mount and offers a manual Refresh.
 */

import { useCallback, useEffect, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Loader2,
  RefreshCw,
  Bot,
  MessagesSquare,
  Users,
  Clock,
} from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { formatDurationMs } from "@/utils/misc";

type Breakdown = Awaited<
  ReturnType<
    ReturnType<typeof useAction<typeof api.jobs.activity.getJobTimeBreakdown>>
  >
>;

export function JobActivityPanel({ jobId }: { jobId: Id<"jobs"> }) {
  const getBreakdown = useAction(api.jobs.activity.getJobTimeBreakdown);
  const [data, setData] = useState<Breakdown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getBreakdown({ jobId });
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load activity");
    } finally {
      setIsLoading(false);
    }
  }, [getBreakdown, jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading && !data) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </Button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col gap-6">
      {/* Header + refresh */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-semibold tracking-tight">Activity</h2>
          <p className="text-sm text-muted-foreground">
            Time spent on this job · as of{" "}
            {new Date(data.computedAt).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* Total card */}
      <div className="rounded-lg border bg-muted/20 p-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          Total time on platform
        </div>
        <div className="mt-1 text-3xl font-semibold tracking-tight">
          {formatDurationMs(data.totalMs)}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            Agents{" "}
            <span className="font-medium text-foreground">
              {formatDurationMs(data.agentProcessingMs)}
            </span>
          </span>
          <span aria-hidden>·</span>
          <span>
            Chat{" "}
            <span className="font-medium text-foreground">
              {formatDurationMs(data.chatMs)}
            </span>
          </span>
        </div>
      </div>

      {/* Agents */}
      <Section icon={Bot} title="Agents (processing)">
        {data.agents.length === 0 ? (
          <EmptyRow>No agent runs yet.</EmptyRow>
        ) : (
          <BreakdownTable
            columns={["Agent", "Time", "Runs", "Last run"]}
            rows={data.agents.map((a) => [
              a.agentName,
              formatDurationMs(a.processingMs),
              String(a.runCount),
              a.lastRunAt
                ? new Date(a.lastRunAt).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                  })
                : "—",
            ])}
          />
        )}
      </Section>

      {/* Agent chats */}
      <Section icon={MessagesSquare} title="Agent chats">
        {data.agentChats.length === 0 ? (
          <EmptyRow>No agent chats yet.</EmptyRow>
        ) : (
          <BreakdownTable
            columns={["Agent", "Time", "Threads", "Messages"]}
            rows={data.agentChats.map((c) => [
              c.agentName,
              formatDurationMs(c.engagementMs),
              String(c.threadCount),
              String(c.messageCount),
            ])}
          />
        )}
      </Section>

      {/* General job chat */}
      <Section icon={MessagesSquare} title="General job chat">
        {data.generalJobChat.threadCount === 0 ? (
          <EmptyRow>No general job chat yet.</EmptyRow>
        ) : (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <Stat
              label="Time"
              value={formatDurationMs(data.generalJobChat.engagementMs)}
            />
            <Stat
              label="Threads"
              value={String(data.generalJobChat.threadCount)}
            />
            <Stat
              label="Messages"
              value={String(data.generalJobChat.messageCount)}
            />
          </div>
        )}
      </Section>

      {/* Contact chats */}
      <Section icon={Users} title="Contact chats">
        {data.contactChats.length === 0 ? (
          <EmptyRow>No contact chats yet.</EmptyRow>
        ) : (
          <BreakdownTable
            columns={["Contact", "Time", "Threads", "Messages"]}
            rows={data.contactChats.map((c) => [
              c.contactName,
              formatDurationMs(c.engagementMs),
              String(c.threadCount),
              String(c.messageCount),
            ])}
          />
        )}
      </Section>

      {/* Footnotes */}
      <div className="flex flex-col gap-1 border-t pt-4 text-xs text-muted-foreground">
        <p>
          Chat time is active engagement — pauses longer than 5 minutes are
          excluded.
        </p>
        {data.truncatedThreadCount > 0 && (
          <p>
            {data.truncatedThreadCount} very long thread
            {data.truncatedThreadCount === 1 ? " was" : "s were"} sampled to the
            first 2,000 messages.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Bot;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground/80">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function BreakdownTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: string[][];
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
            {columns.map((c, i) => (
              <th
                key={c}
                className={
                  i === 0
                    ? "px-3 py-2 font-medium"
                    : "px-3 py-2 font-medium tabular-nums"
                }
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b last:border-0">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={
                    ci === 0
                      ? "px-3 py-2"
                      : "px-3 py-2 tabular-nums text-muted-foreground"
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-muted-foreground">
      {label} <span className="font-medium text-foreground">{value}</span>
    </span>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}
